'use client';

import { useGetIntegrationUserAgentList } from '@/config/accounts/users/api/integrationuseragent/client';
import { usePerms } from '@/hooks/usePerms';
import { getLatestIntegrationService } from '@/lib/getManifestForIntegrationUserAgent';
import { useMemo } from 'react';
import {
  GROUP_LABELS,
  getIntegrationVariantName,
  getIntegrationGroup,
  INTEGRATION_GROUPS,
} from '../groups';
import { deriveAgentStatus } from '../status/derive-status';
import { matchCatalogErrorGroups } from '../status/error-catalog';
import { groupIntegrations } from '../status/group-integrations';
import {
  INTEGRATION_STATUS,
  type IntegrationStatusCategory,
  type IntegrationStatusItem,
} from '../status/types';
import { ActivationLevelMap, getContentTypeVerboseNamePlural } from '../data';
import type {
  IntegrationManifest,
  IntegrationManifestService,
} from '@pigello/pigello-matrix';
import { useIntegrationManifests } from '../use-integration-manifests';
import type { MonitorEventKind } from '@/hooks/integrations/monitor-request';
import {
  monitorEventKindsForService,
  useIntegrationMonitorEvents,
} from './use-integration-monitor-events';

export function useIntegrationStatusPageData(opts?: {
  includeLastSync?: boolean;
  enabled?: boolean;
}) {
  const { canView } = usePerms('integrationuseragent');
  const enabled = opts?.enabled ?? canView;

  const {
    data: agentData,
    isLoading: isLoadingAgents,
    isError: isAgentsError,
  } = useGetIntegrationUserAgentList({
    queryParams: {
      page: 1,
      pageSize: 500,
    },
    enabled,
  });

  const {
    data: manifests,
    isLoading: isLoadingManifests,
    isError: isManifestsError,
  } = useIntegrationManifests({ enabled });

  const agents = useMemo(() => {
    const list = agentData?.list ?? [];
    const byActivation = new Map<string, typeof list>();
    for (const agent of list) {
      const key = `${agent.applicationId}|${agent.serviceId}|${agent.activationObjectId ?? ''}`;
      const group = byActivation.get(key);
      if (group) {
        group.push(agent);
      } else {
        byActivation.set(key, [agent]);
      }
    }
    return [...byActivation.values()].flatMap((group) => {
      const active = group.filter((agent) => agent.isActive !== false);
      if (active.length > 0) return active;
      return [
        group.reduce((newest, candidate) =>
          (candidate.createdAt ?? '') > (newest.createdAt ?? '')
            ? candidate
            : newest
        ),
      ];
    });
  }, [agentData?.list]);

  // Resolved once: the monitor query needs each service to know which event
  // kinds it declares, and the items below need it for name, logo and category.
  const agentServices = useMemo(() => {
    const byAgentId = new Map<
      string,
      {
        manifest: IntegrationManifest;
        service: IntegrationManifestService | undefined;
      }
    >();
    for (const agent of agents) {
      const manifest = manifests?.find(
        (candidate) => candidate.application_id === agent.applicationId
      );
      if (!manifest) continue;
      const serviceVersions = manifest.services[agent.serviceId];
      byAgentId.set(agent.id, {
        manifest,
        service:
          serviceVersions?.[agent.serviceVersionId] ??
          getLatestIntegrationService(
            serviceVersions ?? {},
            manifest.uuid_conversions
          ).service,
      });
    }
    return byAgentId;
  }, [agents, manifests]);

  const eventKindsByAgentId = useMemo(() => {
    const byAgentId = new Map<string, MonitorEventKind[]>();
    for (const [agentId, resolved] of agentServices) {
      byAgentId.set(agentId, monitorEventKindsForService(resolved.service));
    }
    return byAgentId;
  }, [agentServices]);

  const {
    eventsByAgentId,
    lastSyncByAgentId,
    erroredAgentIds,
    pendingAgentIds,
  } = useIntegrationMonitorEvents(agents, {
    enabled: enabled && agents.length > 0 && !!manifests,
    eventKindsByAgentId,
    includeLastSync: opts?.includeLastSync,
  });

  const items = useMemo<IntegrationStatusItem[]>(() => {
    return agents.flatMap((agent) => {
      const resolved = agentServices.get(agent.id);
      if (!resolved) return [];
      const { manifest, service } = resolved;

      const companyName = manifest.company.name;
      const variantName = getIntegrationVariantName(
        companyName,
        service?.public_name
      );

      const categoryKey = getIntegrationGroup(
        agent.applicationId,
        service?.category
      );

      const derived = deriveAgentStatus({
        isActive: agent.isActive,
        events: eventsByAgentId.get(agent.id) ?? [],
        lastSyncAt: lastSyncByAgentId.get(agent.id) ?? null,
        monitorErrored: erroredAgentIds.has(agent.id),
        applicationId: agent.applicationId,
        serviceId: agent.serviceId,
      });

      const activationContentType =
        ActivationLevelMap[service?.activation_level ?? 0];

      return [
        {
          agentId: agent.id,
          applicationId: agent.applicationId,
          serviceId: agent.serviceId,
          companyName,
          variantName,
          logo: service?.logo || undefined,
          categoryKey,
          categoryLabel: GROUP_LABELS[categoryKey],
          activationLabel:
            service?.activation_level === 0
              ? null
              : activationContentType
                ? (getContentTypeVerboseNamePlural(
                    activationContentType
                  )?.toLowerCase() ?? null)
                : null,
          activationObjectId: agent.activationObjectId ?? null,
          status: derived.status,
          statusPending: pendingAgentIds.has(agent.id),
          lastSyncAt: derived.lastSyncAt,
          hasFailures: derived.hasFailures,
          errors:
            derived.status === INTEGRATION_STATUS.ACTION_REQUIRED
              ? matchCatalogErrorGroups(
                  agent.applicationId,
                  agent.serviceId,
                  derived.failingEvents
                ).map((group) => group.entry)
              : [],
          monitorUnavailable: derived.monitorUnavailable,
        } satisfies IntegrationStatusItem,
      ];
    });
  }, [
    agents,
    agentServices,
    eventsByAgentId,
    lastSyncByAgentId,
    erroredAgentIds,
    pendingAgentIds,
  ]);

  const integrationGroups = useMemo(() => groupIntegrations(items), [items]);

  const categories = useMemo<IntegrationStatusCategory[]>(() => {
    return INTEGRATION_GROUPS.flatMap((group) => {
      const integrations = integrationGroups
        .filter((item) => item.categoryKey === group)
        .sort(
          (aItem, bItem) =>
            aItem.companyName.localeCompare(bItem.companyName, 'sv') ||
            (aItem.variantName ?? '').localeCompare(
              bItem.variantName ?? '',
              'sv'
            )
        );
      if (integrations.length === 0) return [];
      return [{ key: group, label: GROUP_LABELS[group], integrations }];
    });
  }, [integrationGroups]);

  const attentionItems = useMemo(
    () =>
      integrationGroups.filter(
        (item) =>
          item.status === INTEGRATION_STATUS.ACTION_REQUIRED &&
          !item.statusPending
      ),
    [integrationGroups]
  );

  return {
    categories,
    attentionItems,
    attentionCount: attentionItems.length,
    isLoading: isLoadingAgents || isLoadingManifests,
    isError: isAgentsError || isManifestsError,
    canView,
  };
}
